import {
  AutotractorPropsType,
  Domain,
  InteractionLayer,
  LayerConfig,
} from "common/components/map/layers/types";
import { createImplementOutline } from "../ImplementOutline";
import { createRobotOutline } from "../RobotOutline";
import { FeatureCollection, Polygon } from "geojson";
import {
  getTractorFeature,
  TRACTOR_POLL_INTERVAL_S,
  TRACTOR_STALE_THRESHOLD_S,
  TractorFeature,
  TractorFeatureProperties,
} from "common/utils/rtcJobs";
import { Layer, Marker, Source } from "react-map-gl";
import {
  LiveRtcTractorResponse,
  useStalePercentage,
} from "common/hooks/useLiveRtcLocation";
import { NumberedTractorIcon } from "common/components/map/NumberedTractorIcon";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import { Tooltip } from "@mui/material";
import { useEffectiveConfig } from "common/hooks/useEffectiveConfig";
import Color from "color";
import NavigationIcon from "@mui/icons-material/Navigation";
import React, { FC } from "react";

export const useTractorLayers = ({
  serials,
  onClick,
  onEnter,
  onLeave,
}: {
  serials?: string[];
  onClick?: (feature?: TractorFeature) => void;
  onEnter?: (feature?: TractorFeature) => void;
  onLeave?: (feature?: TractorFeature) => void;
}): LayerConfig<TractorLayersProps> => {
  const interactionLayers: InteractionLayer[] = serials
    ? serials.flatMap((serial) => [
        {
          id: `tractor-${serial}-layer`,
          cursor: "pointer",
          onClick: (e) => onClick?.(getTractorFeature(e)),
          onMouseMove: (e) => onEnter?.(getTractorFeature(e)),
          onLeave: () => onLeave?.(),
        },
        {
          id: `implement-${serial}-layer`,
          cursor: "pointer",
          onClick: (e) => onClick?.(getTractorFeature(e)),
          onMouseMove: (e) => onEnter?.(getTractorFeature(e)),
          onLeave: () => onLeave?.(),
        },
      ])
    : [];
  return { interactionLayers, Layers: TractorLayers, idsToLoad: [] };
};
interface TractorLayersProps {
  serial: string;
  liveData: LiveRtcTractorResponse;
  color?: string;
  isFocused?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  beforeId?: string;
}

export const TractorLayers: FC<TractorLayersProps> = ({
  serial,
  liveData,
  color,
  onClick,
  isFocused,
  isSelected,
  beforeId,
}) => {
  const { location, isMoving, isStale, lastCheckedTimestamp } = liveData;
  const { effectiveConfig } = useEffectiveConfig(serial);

  const stalePercentage = useStalePercentage(
    TRACTOR_POLL_INTERVAL_S,
    TRACTOR_STALE_THRESHOLD_S,
    lastCheckedTimestamp
  );
  const lng = location?.point?.lng ?? 0;
  const lat = location?.point?.lat ?? 0;
  const heading = location?.headingDegrees ?? 0;
  const tractorOutline = createRobotOutline(lng, lat, heading, effectiveConfig);
  const implementOutlines = effectiveConfig?.implements.map((ic) =>
    createImplementOutline(lng, lat, heading, ic)
  );

  const tractorFeature: TractorFeature = {
    type: "Feature",
    geometry: tractorOutline,
    properties: {
      __portalFeatureProps: true,
      domain: Domain.AUTOTRACTOR,
      type: AutotractorPropsType.TRACTOR,
      serial,
    },
  };
  const implementFeatures: FeatureCollection<
    Polygon,
    TractorFeatureProperties
  > = {
    type: "FeatureCollection",
    features:
      implementOutlines?.map((ib) => ({
        type: "Feature",
        geometry: ib,
        properties: {
          __portalFeatureProps: true,
          domain: Domain.AUTOTRACTOR,
          type: AutotractorPropsType.TRACTOR,
          serial,
        },
      })) ?? [],
  };

  const desaturatedColor = new Color(color)
    .desaturate(isStale ? 1 : Math.min(stalePercentage, 1))
    .toString();

  return (
    <>
      {location?.point && (
        <Marker
          style={{ cursor: "pointer" }}
          key={`${serial}-arrow`}
          latitude={location.point.lat}
          longitude={location.point.lng}
          rotationAlignment="map"
        >
          <Tooltip title={serial} arrow>
            <div className="relative">
              {isMoving && (
                <NavigationIcon
                  fontSize="small"
                  className="absolute font-extrabold"
                  style={{
                    left: "50%",
                    top: "50%",
                    fill: color,
                    stroke: theme.colors.black,
                    strokeWidth: 2,
                    transform: `translate(-50%, -50%) rotate(${location.headingDegrees}deg) translateY(-24px)`,
                  }}
                />
              )}
            </div>
          </Tooltip>
        </Marker>
      )}
      {location?.point && (
        <Marker
          style={{ cursor: "pointer" }}
          key={`${serial}-number`}
          latitude={location.point.lat}
          longitude={location.point.lng}
          onClick={onClick}
        >
          <Tooltip title={serial} arrow>
            <div className="relative">
              <NumberedTractorIcon serial={serial} color={desaturatedColor} />
            </div>
          </Tooltip>
        </Marker>
      )}
      <Source data={implementFeatures} type="geojson">
        {(isFocused || isSelected) && (
          <Layer
            id={`implement-${serial}-outline-2`}
            type="line"
            beforeId={`implement-${serial}-outline`}
            paint={{
              "line-color": isSelected
                ? theme.colors.blue[400]
                : theme.colors.blue[600],
              "line-width": 8,
            }}
          />
        )}
        {isFocused && (
          <Layer
            type="fill"
            id={`implement-${serial}-layer-hover`}
            beforeId={beforeId ?? undefined}
            paint={{
              "fill-opacity": 0.5,
              "fill-color": theme.colors.blue[400],
            }}
          />
        )}
        <Layer
          type="fill"
          id={`implement-${serial}-layer`}
          beforeId={beforeId ?? undefined}
          paint={{
            "fill-color": color ?? theme.colors.black,
          }}
        />
        <Layer
          id={`implement-${serial}-outline`}
          type="line"
          beforeId={`tractor-${serial}-layer`}
          paint={{
            "line-color": theme.colors.black,
            "line-width": 4,
          }}
        />
      </Source>
      <Source data={tractorFeature} type="geojson">
        {isFocused && (
          <Layer
            type="fill"
            id={`tractor-${serial}-layer-hover`}
            beforeId={beforeId ?? undefined}
            paint={{
              "fill-opacity": 0.5,
              "fill-color": theme.colors.blue[400],
            }}
          />
        )}
        <Layer
          type="fill"
          id={`tractor-${serial}-layer`}
          beforeId={beforeId ?? undefined}
          paint={{
            "fill-color": color ?? theme.colors.black,
          }}
        />
        <Layer
          id={`tractor-${serial}-outline`}
          type="line"
          beforeId={`tractor-${serial}-layer`}
          paint={{
            "line-color": theme.colors.black,
            "line-width": 4,
          }}
        />
        {(isFocused || isSelected) && (
          <Layer
            id={`tractor-${serial}-outline-2`}
            type="line"
            beforeId={`tractor-${serial}-outline`}
            paint={{
              "line-color": isSelected
                ? theme.colors.blue[400]
                : theme.colors.blue[600],
              "line-width": 8,
            }}
          />
        )}
      </Source>
    </>
  );
};
